import { useState, useEffect, useRef } from "react";

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const PROJECTS = [
  {
    id: 1, num: "01",
    title: "iCareManager",
    desc: "Cloud-based EHR platform for IDD, assisted living & human services providers — featuring eMAR, care/support planning, compliance reporting, and 50+ integrated modules to streamline long-term care operations.",
    tags: ["Angular", "TypeScript", "REST APIs", "Healthcare", "SaaS"],
    year: "2024", accent: "#00e5ff",
  },
  {
    id: 2, num: "02",
    title: "CableFinder",
    desc: "Industry-first telecom serviceability tool that collapses multiple carrier portals into a single pane — letting agents qualify, quote, and contract cable & fiber services from serviceability to signature in seconds.",
    tags: ["React", "Node.js", "DocuSign API", "Mapbox", "SaaS"],
    year: "2024", accent: "#f59e0b",
  },
  {
    id: 3, num: "03",
    title: "FortressGB",
    desc: "Sports technology platform unifying fan base management, venue operations & marketing into one enterprise solution — covering digital ticketing, automated access control, contactless payments, and loyalty & rewards for 140+ elite teams worldwide.",
    tags: ["Angular", "TypeScript", "Biometrics", "IoT", "Sports Tech"],
    year: "2023", accent: "#a78bfa",
  },
  {
    id: 4, num: "04",
    title: "AsianSol",
    desc: "Pakistan-based software house delivering customized ERP solutions (Accounts & Finance, Supply Chain, POS) and AI-powered security products — including CCTVGuard, featuring real-time face recognition, weapon detection, and anomaly detection.",
    tags: ["Angular", "TypeScript", "ERP", "AI / CV", "PKI Security"],
    year: "2023", accent: "#fb7185",
  },
];

const SKILLS = [
  { name: "Angular",     cat: "Framework",  pct: 95, col: "#00e5ff", span: 2 },
  { name: "TypeScript",  cat: "Language",   pct: 92, col: "#a78bfa", span: 1 },
  { name: "Next.js",     cat: "Framework",  pct: 70, col: "#f59e0b", span: 1 },
  { name: "React",       cat: "Library",    pct: 85, col: "#fb7185", span: 1 },
  { name: "CSS / Sass",  cat: "Styling",    pct: 96, col: "#4ade80", span: 1 },
  { name: "RxJS",        cat: "Reactive",   pct: 88, col: "#00e5ff", span: 2 },
  { name: "Node.js",     cat: "Runtime",    pct: 76, col: "#f59e0b", span: 1 },
  { name: ".NET",       cat: "Framework",     pct: 60, col: "#a78bfa", span: 1 },
];

const ROLES = [
  "Frontend Engineer.",
  "Angular Specialist.",
  "UI / UX Enthusiast.",
  "Next.js Explorer.",
  "Open‑Source Contributor.",
];

const MARQUEE = [
  "Angular","TypeScript","Next.js","React","CSS","Node.js",
  "RxJS","WebGL","Figma","Git","REST APIs","GraphQL","Storybook","Tailwind","D3.js","Mapbox",
];

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=JetBrains+Mono:wght@300;400;500&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

:root{
  --bg:#07070c;
  --bg2:#0e0e16;
  --bg3:#131320;
  --border:rgba(255,255,255,0.06);
  --border2:rgba(255,255,255,0.12);
  --accent:#00e5ff;
  --amber:#f59e0b;
  --violet:#a78bfa;
  --rose:#fb7185;
  --green:#4ade80;
  --txt:#e2e2ee;
  --muted:#4a4a6a;
  --muted2:#2e2e48;
  --display:'Syne',sans-serif;
  --mono:'JetBrains Mono',monospace;
  --body:'DM Sans',sans-serif;
}

html{scroll-behavior:smooth}
body{background:var(--bg);color:var(--txt);font-family:var(--body);cursor:none;overflow-x:hidden;-webkit-font-smoothing:antialiased}

::-webkit-scrollbar{width:3px}
::-webkit-scrollbar-track{background:var(--bg)}
::-webkit-scrollbar-thumb{background:var(--accent);border-radius:2px}

/* ── Cursor ── */
.cur{position:fixed;width:10px;height:10px;background:var(--accent);border-radius:50%;pointer-events:none;z-index:9999;transform:translate(-50%,-50%);transition:width .15s,height .15s;mix-blend-mode:screen}
.cur-ring{position:fixed;width:38px;height:38px;border:1px solid rgba(0,229,255,.35);border-radius:50%;pointer-events:none;z-index:9998;transform:translate(-50%,-50%);transition:width .3s,height .3s,border-color .3s}
.cur.h{width:5px;height:5px}
.cur-ring.h{width:58px;height:58px;border-color:rgba(0,229,255,.6)}

/* ── Nav ── */
.nav{position:fixed;inset:0 0 auto 0;z-index:100;padding:20px 56px;display:flex;align-items:center;justify-content:space-between;transition:background .4s,border-bottom .4s}
.nav.s{background:rgba(7,7,12,.88);backdrop-filter:blur(24px);border-bottom:1px solid var(--border)}
.logo{font-family:var(--mono);font-size:13px;font-weight:500;color:var(--txt);letter-spacing:.12em}
.logo em{color:var(--accent);font-style:normal}
.nav-links{display:flex;gap:40px;list-style:none}
.nav-links a{font-family:var(--mono);font-size:11px;color:var(--muted);text-decoration:none;letter-spacing:.1em;text-transform:uppercase;transition:color .2s;position:relative}
.nav-links a::after{content:'';position:absolute;bottom:-3px;left:0;right:0;height:1px;background:var(--accent);transform:scaleX(0);transition:transform .25s;transform-origin:left}
.nav-links a:hover{color:var(--txt)}
.nav-links a:hover::after{transform:scaleX(1)}
.nav-btn{font-family:var(--mono);font-size:11px;font-weight:500;letter-spacing:.12em;text-transform:uppercase;color:var(--bg);background:var(--accent);border:none;padding:10px 24px;border-radius:2px;cursor:none;transition:opacity .2s,transform .2s}
.nav-btn:hover{opacity:.85;transform:translateY(-1px)}

/* ── Hero ── */
.hero{min-height:100vh;display:flex;flex-direction:column;justify-content:center;padding:120px 56px 100px;position:relative;overflow:hidden}
.hero-bg{position:absolute;inset:0;background-image:
  radial-gradient(ellipse 90% 70% at 50% -10%,rgba(0,229,255,.07) 0%,transparent 60%),
  radial-gradient(ellipse 50% 40% at 90% 80%,rgba(167,139,250,.05) 0%,transparent 50%),
  linear-gradient(rgba(255,255,255,.012) 1px,transparent 1px),
  linear-gradient(90deg,rgba(255,255,255,.012) 1px,transparent 1px);
  background-size:auto,auto,64px 64px,64px 64px;
  background-position:0 0,0 0,-1px -1px,-1px -1px}

.hero-noise{position:absolute;inset:0;opacity:.03;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");background-size:200px 200px;pointer-events:none}

.hero-tag{font-family:var(--mono);font-size:10px;color:var(--accent);letter-spacing:.22em;text-transform:uppercase;margin-bottom:28px;display:flex;align-items:center;gap:14px}
.hero-tag::before{content:'';width:36px;height:1px;background:var(--accent)}
.hero-tag-dot{width:6px;height:6px;border-radius:50%;background:var(--green);animation:pulse 2s infinite}
@keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(74,222,128,.4)}50%{box-shadow:0 0 0 6px rgba(74,222,128,0)}}

.hero-name{font-family:var(--display);font-size:clamp(68px,10.5vw,156px);font-weight:800;line-height:.88;letter-spacing:-.035em;color:var(--txt);margin-bottom:36px;position:relative;user-select:none}
.hero-name .outline{color:transparent;-webkit-text-stroke:1px rgba(255,255,255,.18)}

@keyframes glitch{
  0%,92%,100%{clip-path:none;transform:none;color:inherit}
  93%{clip-path:polygon(0 10%,100% 10%,100% 28%,0 28%);transform:translate(-4px,0);color:var(--accent)}
  94%{clip-path:polygon(0 60%,100% 60%,100% 72%,0 72%);transform:translate(4px,0);color:var(--amber)}
  95%{clip-path:polygon(0 38%,100% 38%,100% 44%,0 44%);transform:translate(-2px,0)}
  96%{clip-path:none;transform:translate(2px,0)}
  97%{clip-path:polygon(0 80%,100% 80%,100% 90%,0 90%);transform:translate(-3px,0);color:var(--violet)}
  98%,99%{clip-path:none;transform:none}
}
.hero-name.glitch{animation:glitch .55s ease}

.hero-role{font-family:var(--mono);font-size:clamp(13px,1.4vw,17px);color:var(--muted);margin-bottom:48px;line-height:1.6;max-width:540px}
.hero-role .typed{color:var(--txt);font-weight:500}
.cursor-blink{color:var(--accent);animation:blink 1s step-end infinite}
@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}

.hero-ctas{display:flex;gap:16px;flex-wrap:wrap}
.btn-p{font-family:var(--mono);font-size:11px;font-weight:500;letter-spacing:.12em;text-transform:uppercase;color:var(--bg);background:var(--accent);border:none;padding:15px 34px;border-radius:2px;cursor:none;transition:opacity .2s,transform .2s;text-decoration:none;display:inline-flex;align-items:center;gap:8px}
.btn-p:hover{opacity:.85;transform:translateY(-2px)}
.btn-s{font-family:var(--mono);font-size:11px;font-weight:400;letter-spacing:.12em;text-transform:uppercase;color:var(--txt);background:transparent;border:1px solid var(--border2);padding:15px 34px;border-radius:2px;cursor:none;transition:border-color .2s,color .2s,transform .2s;text-decoration:none;display:inline-flex;align-items:center;gap:8px}
.btn-s:hover{border-color:var(--accent);color:var(--accent);transform:translateY(-2px)}

.hero-scroll{position:absolute;bottom:40px;left:56px;display:flex;align-items:center;gap:14px;font-family:var(--mono);font-size:9px;color:var(--muted);letter-spacing:.2em;text-transform:uppercase}
.scroll-line{width:56px;height:1px;background:var(--muted2);position:relative;overflow:hidden}
.scroll-line::after{content:'';position:absolute;inset:0 auto 0 -100%;width:50%;background:var(--accent);animation:sl 2.2s linear infinite}
@keyframes sl{to{left:200%}}

.hero-stats{position:absolute;right:56px;bottom:40px;display:flex;gap:40px}
.stat{text-align:right}
.stat-n{font-family:var(--display);font-size:28px;font-weight:800;color:var(--txt);letter-spacing:-.03em;line-height:1}
.stat-l{font-family:var(--mono);font-size:9px;color:var(--muted);letter-spacing:.12em;text-transform:uppercase;margin-top:4px}

/* ── Marquee ── */
.marquee-wrap{border-top:1px solid var(--border);border-bottom:1px solid var(--border);padding:14px 0;overflow:hidden;background:rgba(0,229,255,.018)}
.marquee-track{display:flex;animation:mq 22s linear infinite;white-space:nowrap}
.mq-item{font-family:var(--mono);font-size:10px;color:var(--muted);letter-spacing:.18em;text-transform:uppercase;padding:0 28px;display:flex;align-items:center;gap:28px}
.mq-item::after{content:'✦';color:var(--accent);font-size:8px}
@keyframes mq{from{transform:translateX(0)}to{transform:translateX(-50%)}}

/* ── Section shared ── */
.sec{padding:120px 56px;max-width:1440px;margin:0 auto}
.sec-label{font-family:var(--mono);font-size:9px;color:var(--accent);letter-spacing:.28em;text-transform:uppercase;margin-bottom:14px;display:flex;align-items:center;gap:14px}
.sec-label::before{content:'';width:28px;height:1px;background:var(--accent)}
.sec-title{font-family:var(--display);font-size:clamp(38px,5.5vw,72px);font-weight:800;line-height:.95;letter-spacing:-.025em;color:var(--txt);margin-bottom:72px}

/* ── Projects ── */
.proj-grid{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--border);border:1px solid var(--border)}
.proj-card{background:var(--bg2);padding:52px 48px;position:relative;overflow:hidden;cursor:none;transition:background .3s}
.proj-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--ca,var(--accent));transform:scaleX(0);transition:transform .45s ease;transform-origin:left}
.proj-card:hover{background:var(--bg3)}
.proj-card:hover::before{transform:scaleX(1)}
.proj-ghost{font-family:var(--display);font-size:96px;font-weight:800;line-height:1;color:rgba(255,255,255,.03);position:absolute;top:20px;right:24px;letter-spacing:-.05em;transition:color .35s;pointer-events:none;user-select:none}
.proj-card:hover .proj-ghost{color:rgba(255,255,255,.07)}
.proj-yr{font-family:var(--mono);font-size:9px;color:var(--muted);letter-spacing:.12em;margin-bottom:28px}
.proj-title{font-family:var(--display);font-size:clamp(22px,2.4vw,32px);font-weight:700;color:var(--txt);margin-bottom:16px;line-height:1.1}
.proj-desc{font-size:14px;color:var(--muted);line-height:1.75;margin-bottom:32px;max-width:380px}
.proj-tags{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:52px}
.tag{font-family:var(--mono);font-size:9px;letter-spacing:.08em;color:var(--muted);border:1px solid var(--border);padding:4px 10px;border-radius:2px;transition:color .2s,border-color .2s}
.proj-card:hover .tag{border-color:var(--border2);color:rgba(226,226,238,.7)}
.proj-arrow{position:absolute;bottom:32px;right:32px;width:38px;height:38px;border:1px solid var(--border);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:15px;color:var(--muted);transition:all .3s}
.proj-card:hover .proj-arrow{border-color:var(--ca,var(--accent));color:var(--ca,var(--accent));transform:translate(2px,-2px)}

/* ── Skills bento ── */
.bento{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:var(--border);border:1px solid var(--border)}
.skill-cell{background:var(--bg2);padding:36px 32px;cursor:none;position:relative;overflow:hidden;transition:background .3s}
.skill-cell.w2{grid-column:span 2}
.skill-cell:hover{background:var(--bg3)}
.skill-cell::after{content:'';position:absolute;inset:0;background:radial-gradient(ellipse 100% 100% at var(--mx,50%) var(--my,50%),rgba(255,255,255,.04) 0%,transparent 70%);opacity:0;transition:opacity .3s}
.skill-cell:hover::after{opacity:1}
.sk-name{font-family:var(--display);font-size:22px;font-weight:700;color:var(--txt);margin-bottom:6px}
.sk-cat{font-family:var(--mono);font-size:9px;color:var(--muted);letter-spacing:.16em;text-transform:uppercase;margin-bottom:24px}
.bar-track{height:2px;background:var(--muted2);border-radius:1px;overflow:hidden}
.bar-fill{height:100%;border-radius:1px;transition:width 1.4s cubic-bezier(.16,1,.3,1);width:0}
.bar-fill.loaded{width:var(--w)}
.sk-pct{font-family:var(--mono);font-size:10px;color:var(--muted);margin-top:10px}

/* ── About ── */
.about-grid{display:grid;grid-template-columns:340px 1fr;gap:100px;align-items:start}
.about-left{position:sticky;top:120px}
.avatar-wrap{aspect-ratio:3/4;background:var(--bg2);border:1px solid var(--border);position:relative;overflow:hidden;margin-bottom:28px}
.avatar-init{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-family:var(--display);font-size:96px;font-weight:800;color:rgba(255,255,255,.04);letter-spacing:-.04em;user-select:none}
.avatar-wrap::before{content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(0,229,255,.08) 0%,transparent 50%);z-index:1}
.avatar-wrap::after{content:'SHOAIB KHALIL';position:absolute;bottom:0;left:0;right:0;font-family:var(--mono);font-size:9px;letter-spacing:.15em;text-transform:uppercase;color:rgba(255,255,255,.25);padding:16px;background:linear-gradient(transparent,rgba(0,0,0,.6));z-index:2}
.about-meta{font-family:var(--mono);font-size:11px;color:var(--muted);line-height:2.2;border-top:1px solid var(--border);padding-top:20px}
.about-meta strong{color:var(--txt);font-weight:500;float:right}
.status-dot{display:inline-block;width:6px;height:6px;border-radius:50%;background:var(--green);margin-right:6px;animation:pulse 2s infinite;vertical-align:middle}

.bio p{font-size:16px;line-height:1.85;color:rgba(226,226,238,.62);margin-bottom:28px}
.bio .lead{font-family:var(--display);font-size:clamp(21px,2.4vw,28px);font-weight:700;color:var(--txt);line-height:1.35}
.hl{color:var(--accent)}
.bio-ctas{display:flex;gap:16px;margin-top:48px;flex-wrap:wrap}

/* ── Contact ── */
.contact-wrap{padding:120px 56px;background:var(--bg2);border-top:1px solid var(--border);text-align:center}
.contact-hint{font-family:var(--mono);font-size:11px;color:var(--muted);letter-spacing:.1em;margin:16px 0 40px}
.contact-email{font-family:var(--display);font-size:clamp(28px,5vw,72px);font-weight:800;color:var(--txt);text-decoration:none;letter-spacing:-.03em;transition:color .3s;display:inline-block;position:relative}
.contact-email::after{content:'';position:absolute;bottom:2px;left:0;right:0;height:3px;background:var(--accent);transform:scaleX(0);transition:transform .45s ease;transform-origin:left}
.contact-email:hover{color:var(--accent)}
.contact-email:hover::after{transform:scaleX(1)}
.socials{display:flex;justify-content:center;gap:28px;margin-top:52px;flex-wrap:wrap}
.soc-link{font-family:var(--mono);font-size:10px;color:var(--muted);text-decoration:none;letter-spacing:.12em;text-transform:uppercase;transition:color .2s;display:flex;align-items:center;gap:6px}
.soc-link:hover{color:var(--accent)}

/* ── Footer ── */
.footer{padding:28px 56px;border-top:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;max-width:1440px;margin:0 auto}
.foot-txt{font-family:var(--mono);font-size:10px;color:var(--muted);letter-spacing:.08em}

/* ── Fade animations ── */
@keyframes fadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
.fu{animation:fadeUp .9s ease forwards;opacity:0}
.d1{animation-delay:.08s}.d2{animation-delay:.22s}.d3{animation-delay:.4s}.d4{animation-delay:.58s}.d5{animation-delay:.76s}.d6{animation-delay:.94s}

/* ── Responsive ── */
@media(max-width:900px){
  .nav{padding:16px 24px}
  .nav-links{display:none}
  .hero{padding:100px 24px 90px}
  .hero-stats{display:none}
  .hero-scroll{left:24px;bottom:28px}
  .sec{padding:80px 24px}
  .proj-grid{grid-template-columns:1fr}
  .bento{grid-template-columns:repeat(2,1fr)}
  .skill-cell.w2{grid-column:span 1}
  .about-grid{grid-template-columns:1fr;gap:48px}
  .about-left{position:static}
  .contact-wrap{padding:80px 24px}
  .footer{padding:24px;flex-direction:column;gap:12px;text-align:center}
}
`;

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
export default function Portfolio() {
  /* cursor */
  const [cur, setCur]   = useState({ x: -200, y: -200 });
  const [ring, setRing] = useState({ x: -200, y: -200 });
  const [hov, setHov]   = useState(false);

  /* nav */
  const [scrolled, setScrolled] = useState(false);

  /* typing */
  const [typed, setTyped]   = useState("");
  const [rIdx, setRIdx]     = useState(0);
  const [cIdx, setCIdx]     = useState(0);
  const [del, setDel]       = useState(false);

  /* glitch */
  const [glitch, setGlitch] = useState(false);

  /* skill bars */
  const [barsOn, setBarsOn] = useState(false);
  const skillRef = useRef(null);

  /* ── cursor logic ── */
  useEffect(() => {
    let tx = -200, ty = -200, rx = -200, ry = -200, raf;
    const mv = (e) => { tx = e.clientX; ty = e.clientY; setCur({ x: tx, y: ty }); };
    const tick = () => {
      rx += (tx - rx) * 0.11;
      ry += (ty - ry) * 0.11;
      setRing({ x: rx, y: ry });
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", mv);
    raf = requestAnimationFrame(tick);
    return () => { window.removeEventListener("mousemove", mv); cancelAnimationFrame(raf); };
  }, []);

  /* ── scroll ── */
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  /* ── typing ── */
  useEffect(() => {
    const r = ROLES[rIdx], speed = del ? 42 : 95;
    const t = setTimeout(() => {
      if (!del) {
        if (cIdx < r.length) { setTyped(r.slice(0, cIdx + 1)); setCIdx(c => c + 1); }
        else setTimeout(() => setDel(true), 2200);
      } else {
        if (cIdx > 0) { setTyped(r.slice(0, cIdx - 1)); setCIdx(c => c - 1); }
        else { setDel(false); setRIdx(i => (i + 1) % ROLES.length); }
      }
    }, speed);
    return () => clearTimeout(t);
  }, [cIdx, del, rIdx]);

  /* ── glitch ── */
  useEffect(() => {
    const id = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 560);
    }, 5500);
    return () => clearInterval(id);
  }, []);

  /* ── skill bars IntersectionObserver ── */
  useEffect(() => {
    const el = skillRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setBarsOn(true); }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const H = () => setHov(true);
  const L = () => setHov(false);

  /* ── render ── */
  return (
    <>
      <style>{CSS}</style>

      {/* Custom cursor */}
      <div className={`cur${hov ? " h" : ""}`} style={{ left: cur.x, top: cur.y }} />
      <div className={`cur-ring${hov ? " h" : ""}`} style={{ left: ring.x, top: ring.y }} />

      {/* ────── NAV ────── */}
      <nav className={`nav${scrolled ? " s" : ""}`}>
        <div className="logo">SK<em>.</em></div>
        <ul className="nav-links">
          {["Work","Skills","About","Contact"].map(l => (
            <li key={l}><a href={`#${l.toLowerCase()}`} onMouseEnter={H} onMouseLeave={L}>{l}</a></li>
          ))}
        </ul>
        <button className="nav-btn" onMouseEnter={H} onMouseLeave={L} onClick={() => window.open("https://wa.me/923054381826", "_blank")}>Hire Me →</button>
      </nav>

      {/* ────── HERO ────── */}
      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-noise" />

        <div className="hero-tag fu d1">
          <span className="hero-tag-dot" />
          Available for Freelance &amp; Full‑time
        </div>

        <h1 className={`hero-name fu d2${glitch ? " glitch" : ""}`}>
          SHOAIB<br />
          <span className="outline">KHALIL</span>
        </h1>

        <div className="hero-role fu d3">
          <span className="typed">{typed}</span>
          <span className="cursor-blink">|</span>
          <br /><br />
          Crafting exceptional digital experiences with cutting‑edge technologies.
          Based in <span style={{ color: "var(--txt)" }}>Lahore, Pakistan 🇵🇰</span>
        </div>

        <div className="hero-ctas fu d4">
          <a href="#work" className="btn-p" onMouseEnter={H} onMouseLeave={L}>View Work ↓</a>
          <a href="#contact" className="btn-s" onMouseEnter={H} onMouseLeave={L}>Get in Touch</a>
        </div>

        <div className="hero-scroll fu d5">
          <div className="scroll-line" />
          Scroll
        </div>

        <div className="hero-stats fu d5">
          {[["5+","Years Exp"],["20+","Projects Built"],["∞","Curiosity"]].map(([n,l])=>(
            <div className="stat" key={l}>
              <div className="stat-n">{n}</div>
              <div className="stat-l">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ────── MARQUEE ────── */}
      <div className="marquee-wrap">
        <div className="marquee-track">
          {[...MARQUEE,...MARQUEE].map((t,i) => (
            <span className="mq-item" key={i}>{t}</span>
          ))}
        </div>
      </div>

      {/* ────── PROJECTS ────── */}
      <div id="work" style={{ background:"var(--bg)" }}>
        <div className="sec">
          <div className="sec-label">Selected Work</div>
          <h2 className="sec-title">Things I've<br />Built</h2>
          <div className="proj-grid">
            {PROJECTS.map((p) => (
              <div key={p.id} className="proj-card" style={{ "--ca": p.accent }} onMouseEnter={H} onMouseLeave={L}>
                <div className="proj-ghost">{p.num}</div>
                <div className="proj-yr">{p.year}</div>
                <h3 className="proj-title">{p.title}</h3>
                <p className="proj-desc">{p.desc}</p>
                <div className="proj-tags">{p.tags.map(t=><span className="tag" key={t}>{t}</span>)}</div>
                <div className="proj-arrow">↗</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ────── SKILLS ────── */}
      <div id="skills" style={{ background:"var(--bg2)", borderTop:"1px solid var(--border)", borderBottom:"1px solid var(--border)" }}>
        <div className="sec" ref={skillRef}>
          <div className="sec-label">Expertise</div>
          <h2 className="sec-title">My Tech<br />Stack</h2>
          <div className="bento">
            {SKILLS.map((s,i) => (
              <div key={s.name} className={`skill-cell${s.span===2?" w2":""}`} onMouseEnter={H} onMouseLeave={L}>
                <div className="sk-name">{s.name}</div>
                <div className="sk-cat">{s.cat}</div>
                <div className="bar-track">
                  <div
                    className={`bar-fill${barsOn?" loaded":""}`}
                    style={{ "--w":`${s.pct}%`, background: s.col, transitionDelay:`${i*0.07}s` }}
                  />
                </div>
                <div className="sk-pct">{s.pct}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ────── ABOUT ────── */}
      <div id="about" style={{ background:"var(--bg)" }}>
        <div className="sec">
          <div className="sec-label">Who I Am</div>
          <h2 className="sec-title">About Me</h2>
          <div className="about-grid">
            <div className="about-left">
              <div className="avatar-wrap">
                <div className="avatar-init">SK</div>
              </div>
              <div className="about-meta">
                <div>Name <strong>Shoaib Khalil</strong></div>
                <div>Role <strong>Frontend Engineer</strong></div>
                <div>Location <strong>Lahore, PK</strong></div>
                <div>Focus <strong>Angular · Next.js</strong></div>
                <div>Status <strong><span className="status-dot" />Available</strong></div>
              </div>
            </div>
            <div className="bio">
              <p className="lead">
                I'm a <span className="hl">frontend-focused engineer</span> who
                obsesses over pixels, performance, and developer experience.
              </p>
              <p>
                With 5+ years of hands-on experience, I specialize in building
                scalable <span style={{color:"var(--txt)"}}>Angular applications</span> and
                increasingly love exploring the Next.js ecosystem. I care deeply
                about the craft — from architecture decisions down to micro-animations
                that make an interface feel alive.
              </p>
              <p>
                I believe the best interfaces are the ones users don't notice —
                because they just <em>work</em>, feel natural, and bring joy. Always
                hunting for the next tool or idea that makes building the web more exciting.
              </p>
              <p>
                When not writing code, I'm dissecting other people's portfolios,
                reading about design systems at 2am, or tinkering with new frameworks
                purely for the joy of it.
              </p>
              <div className="bio-ctas">
                <a href="#contact" className="btn-p" onMouseEnter={H} onMouseLeave={L}>Let's Work Together</a>
                <a href="/shoaib_khalil_cv.pdf" target="_blank" rel="noreferrer" className="btn-s" onMouseEnter={H} onMouseLeave={L}>Download CV</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ────── CONTACT ────── */}
      <div id="contact" className="contact-wrap">
        <div className="sec-label" style={{ justifyContent:"center" }}>Get in Touch</div>
        <div className="contact-hint">Have a project in mind? Let's make something great.</div>
        <a href="mailto:shoaibkhalil111@gmail.com" className="contact-email" onMouseEnter={H} onMouseLeave={L}>
          shoaibkhalil111@gmail.com
        </a>
        <div className="socials">
          {[["GitHub","https://github.com/realshoaib"],["LinkedIn","https://www.linkedin.com/in/shoaibkhalil"],["Twitter","https://x.com/akashoaibkhalil"],["Instagram","https://www.instagram.com/shoaibkhalil"]].map(([n,h])=>(
            <a key={n} href={h} className="soc-link" onMouseEnter={H} onMouseLeave={L}>{n} ↗</a>
          ))}
        </div>
      </div>

      {/* ────── FOOTER ────── */}
      <footer>
        <div className="footer">
          <div className="foot-txt">© {new Date().getFullYear()} Shoaib Khalil. All rights reserved.</div>
          <div className="foot-txt" style={{ color:"var(--accent)",letterSpacing:".05em" }}>SK.</div>
          <div className="foot-txt">Crafted with care in Lahore 🇵🇰</div>
        </div>
      </footer>
    </>
  );
}
